import api from "@/lib/axiosConfig";
import { type SupportedLocale } from "@/i18n";
import type { Data } from "@measured/puck";
import { BASE, type PuckPageApi, toRecord } from "@/api/cms-puck/client";
import { dehydrate, dehydrateForSave } from "@/api/cms-puck/content-i18n";
import type {
  LegacyMigrationKind,
  MigrationStatusItem,
  PuckPageListItem,
  PuckPageRecord,
  PuckPageStatus,
  PuckPublicationItem,
  PuckRoute,
} from "@/api/cms-puck/types";

/* -------------------------------------------------------------------------- */
/* Pages : CRUD + publications + preview                                        */
/* -------------------------------------------------------------------------- */

export async function listPuckPages(options?: {
  excludeCatalogFlow?: boolean;
  excludeMemberFlow?: boolean;
  excludeShellFlow?: boolean;
  excludeSearchFlow?: boolean;
  excludeMaintenancePage?: boolean;
  excludeAbonnementPage?: boolean;
}): Promise<PuckPageListItem[]> {
  const params: Record<string, string> = {};
  if (options?.excludeCatalogFlow) params.excludeCatalogFlow = "true";
  if (options?.excludeMemberFlow) params.excludeMemberFlow = "true";
  if (options?.excludeShellFlow) params.excludeShellFlow = "true";
  if (options?.excludeSearchFlow) params.excludeSearchFlow = "true";
  if (options?.excludeMaintenancePage) params.excludeMaintenancePage = "true";
  if (options?.excludeAbonnementPage) params.excludeAbonnementPage = "true";
  const { data } = await api.get<PuckPageListItem[]>(`${BASE}/pages`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

/**
 * Charge une page pour `lang` : l'API renvoie le layout neutre + le dictionnaire
 * `strings` de la langue → on réhydrate pour que Puck affiche le bon texte.
 * Sans `lang` : ancien comportement (layout tel quel, pas de strings).
 */
export async function loadPuckPage(
  id: string,
  lang?: SupportedLocale,
): Promise<PuckPageRecord> {
  const { data } = await api.get<PuckPageApi>(
    `${BASE}/pages/${encodeURIComponent(id)}`,
    { params: lang ? { lang } : undefined },
  );
  return toRecord(data);
}

/**
 * Crée une page. `data` est le doc Puck d'édition (texte en clair) ; on le
 * déshydrate → layout neutre + strings de `lang` envoyés à l'API.
 */
export async function createPuckPage(input: {
  name: string;
  data: Data;
  lang?: SupportedLocale;
}): Promise<PuckPageRecord> {
  const { neutralLayout, strings } = dehydrate(input.data);
  const { data } = await api.post<PuckPageApi>(`${BASE}/pages`, {
    name: input.name,
    data: neutralLayout,
    ...(input.lang ? { strings, lang: input.lang } : {}),
  });
  return toRecord(data);
}

/**
 * Crée une page depuis un modèle de la bibliothèque. Le layout et le contenu
 * type (toutes langues) sont seedés côté API depuis le registre ; on n'envoie
 * que le nom et la clé du modèle. La page renvoyée porte `sourceTemplateKey`.
 */
export async function createPuckPageFromTemplate(input: {
  name: string;
  templateKey: string;
}): Promise<PuckPageRecord> {
  const { data } = await api.post<PuckPageApi>(`${BASE}/pages`, {
    name: input.name,
    data: {},
    templateKey: input.templateKey,
  });
  return toRecord(data);
}

/**
 * Ne garde que les clés dont la valeur diffère du baseline (= ce qui était
 * affiché au chargement, fallback inclus). → on n'écrit dans `ContentString` que
 * les champs RÉELLEMENT traduits/modifiés ; les champs laissés au fallback ne
 * sont pas figés en copies, le fallback « live » au rendu est préservé.
 */
function diffStrings(
  current: Record<string, string>,
  baseline: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(current)) {
    if (value !== (baseline[key] ?? "")) out[key] = value;
  }
  return out;
}

/**
 * Sauvegarde une page. `data` est le doc Puck d'édition (texte en clair pour
 * `lang`) ; déshydraté → layout neutre (structure partagée) + strings de `lang`.
 */
export async function savePuckPage(
  id: string,
  input: {
    name: string;
    status?: PuckPageStatus;
    data: Data;
    lang?: SupportedLocale;
    /** Strings chargées (fallback inclus) → diff pour n'écrire que les modifs. */
    baselineStrings?: Record<string, string>;
    /** Langue par défaut de l'instance (images par langue : base vs surcharge). */
    defaultLang?: SupportedLocale;
    /** Layout NEUTRE chargé (base des champs média) — cf. PER_LANGUAGE_IMAGES.md. */
    base?: Data;
  },
): Promise<void> {
  // Images par langue : si on connaît la langue courante, la langue par défaut et
  // la base, on gère base + surcharges ; sinon comportement texte historique.
  const { neutralLayout, strings } =
    input.lang && input.defaultLang && input.base
      ? dehydrateForSave(input.data, {
          lang: input.lang,
          defaultLang: input.defaultLang,
          base: input.base,
        })
      : dehydrate(input.data);
  const toSend = input.baselineStrings
    ? diffStrings(strings, input.baselineStrings)
    : strings;
  await api.put(`${BASE}/pages/${encodeURIComponent(id)}`, {
    name: input.name,
    status: input.status,
    data: neutralLayout,
    ...(input.lang ? { strings: toSend, lang: input.lang } : {}),
  });
}

export async function publishPuckPage(id: string): Promise<void> {
  await api.post(`${BASE}/pages/${encodeURIComponent(id)}/publish`);
}

/** Repasse une page publiée en brouillon (retire le snapshot public). */
export async function unpublishPuckPage(id: string): Promise<void> {
  await api.post(`${BASE}/pages/${encodeURIComponent(id)}/unpublish`);
}

export async function archivePuckPage(id: string): Promise<void> {
  await api.post(`${BASE}/pages/${encodeURIComponent(id)}/archive`);
}

export async function deletePuckPage(id: string): Promise<void> {
  await api.delete(`${BASE}/pages/${encodeURIComponent(id)}`);
}

/** Duplique une page (layout + traductions + routes). */
export async function duplicatePuckPage(
  id: string,
  input?: { name?: string },
): Promise<PuckPageRecord> {
  const { data } = await api.post<PuckPageApi>(
    `${BASE}/pages/${encodeURIComponent(id)}/duplicate`,
    input ?? {},
  );
  return toRecord(data);
}

export async function listPagePublications(
  pageId: string,
): Promise<PuckPublicationItem[]> {
  const { data } = await api.get<PuckPublicationItem[]>(
    `${BASE}/pages/${encodeURIComponent(pageId)}/publications`,
  );
  return data;
}

export async function restorePagePublication(
  pageId: string,
  publicationId: string,
  lang: SupportedLocale,
): Promise<PuckPageRecord> {
  const { data } = await api.post<PuckPageApi>(
    `${BASE}/pages/${encodeURIComponent(pageId)}/publications/${encodeURIComponent(publicationId)}/restore`,
    undefined,
    { params: { lang } },
  );
  return toRecord(data);
}

export async function getPagePreviewUrl(
  pageId: string,
  lang: SupportedLocale,
): Promise<{ url: string; slug: string | null; expiresInSeconds: number }> {
  const { data } = await api.get<{
    url: string;
    slug: string | null;
    expiresInSeconds: number;
  }>(`${BASE}/pages/${encodeURIComponent(pageId)}/preview-url`, {
    params: { lang },
  });
  return data;
}

/**
 * URL d'entrée du MODE APERÇU du site public : l'accueil, avec les BROUILLONS
 * du CMS avancé (en-tête, pied de page, pages, menus) rendus à la place du site
 * publié. Rien n'est publié : seul le navigateur qui ouvre ce lien voit les
 * brouillons, les visiteurs continuent de recevoir le site en ligne.
 */
export async function getDraftSiteUrl(): Promise<{
  url: string;
  expiresInSeconds: number;
}> {
  const { data } = await api.get<{ url: string; expiresInSeconds: number }>(
    `${BASE}/draft-site-url`,
  );
  return data;
}

/* -------------------------------------------------------------------------- */
/* Migration depuis l'ancien CMS                                               */
/* -------------------------------------------------------------------------- */

export async function getMigrationStatus(): Promise<{
  homePageId: string | null;
  items: MigrationStatusItem[];
}> {
  const { data } = await api.get<{
    homePageId: string | null;
    items: MigrationStatusItem[];
  }>(`${BASE}/migration/status`);
  return data;
}

export async function migrateFromLegacy(input: {
  kind: LegacyMigrationKind;
  targetPageId?: string;
  setAsHome?: boolean;
}): Promise<{ page: PuckPageApi; kind: LegacyMigrationKind; setAsHome: boolean }> {
  const { data } = await api.post<{
    page: PuckPageApi;
    kind: LegacyMigrationKind;
    setAsHome: boolean;
  }>(`${BASE}/migration/legacy`, input);
  return data;
}

/** Récap d'un item importé lors de l'import global de l'ancien site. */
export interface ImportedLegacyItem {
  kind: LegacyMigrationKind;
  pageId: string;
  name: string;
}

/**
 * Import global de l'ancien site : migre plusieurs pages legacy en une passe
 * (premier lancement). Chaque item devient une page CMS avancé brouillon.
 */
export async function importLegacySite(
  items: { kind: LegacyMigrationKind; setAsHome?: boolean }[],
): Promise<{
  imported: ImportedLegacyItem[];
  skipped: LegacyMigrationKind[];
  count: number;
}> {
  const { data } = await api.post<{
    imported: ImportedLegacyItem[];
    skipped: LegacyMigrationKind[];
    count: number;
  }>(`${BASE}/migration/import-site`, { items });
  return data;
}

/**
 * R2 — Copie les `ContentString` des blocs dupliqués vers leurs nouveaux ids
 * (toutes langues). `mappings` = `{ ancienBlockId: nouveauBlockId }`.
 */
export async function copyPuckBlockStrings(
  pageId: string,
  mappings: Record<string, string>,
): Promise<{ copied: number }> {
  const { data } = await api.post<{ copied: number }>(
    `${BASE}/pages/${encodeURIComponent(pageId)}/strings/copy-blocks`,
    { mappings },
  );
  return data;
}

/**
 * Copier/coller cross-page : transfère les `ContentString` depuis `sourcePageId`
 * vers la page cible (toutes langues), selon les mappings d'ids de blocs.
 */
export async function transferPuckBlockStrings(
  targetPageId: string,
  input: { sourcePageId: string; mappings: Record<string, string> },
): Promise<{ copied: number }> {
  const { data } = await api.post<{ copied: number }>(
    `${BASE}/pages/${encodeURIComponent(targetPageId)}/strings/transfer-blocks`,
    input,
  );
  return data;
}

/* -------------------------------------------------------------------------- */
/* Routing localisé : slugs par langue + page d'accueil                       */
/* -------------------------------------------------------------------------- */

/** Routes (toutes langues) d'une page. */
export async function getPageRoutes(pageId: string): Promise<PuckRoute[]> {
  const { data } = await api.get<PuckRoute[]>(
    `${BASE}/pages/${encodeURIComponent(pageId)}/routes`,
  );
  return data;
}

/**
 * Upsert du slug (+ seo) d'une route (page, langue). Renvoie la liste à jour.
 * Le backend slugifie ; un 409 signale un slug déjà pris pour cette langue.
 */
export async function upsertPageRoute(
  pageId: string,
  lang: SupportedLocale,
  input: { slug: string; seo?: Record<string, unknown> },
): Promise<PuckRoute[]> {
  const { data } = await api.put<PuckRoute[]>(
    `${BASE}/pages/${encodeURIComponent(pageId)}/routes/${encodeURIComponent(lang)}`,
    { slug: input.slug, seo: input.seo },
  );
  return data;
}

/** Page d'accueil courante du tenant. */
export async function getHomePage(): Promise<{ homePageId: string | null }> {
  const { data } = await api.get<{ homePageId: string | null }>(`${BASE}/home`);
  return data;
}

/** Désigne une page comme page d'accueil. */
export async function setHomePage(
  pageId: string,
): Promise<{ homePageId: string | null }> {
  const { data } = await api.put<{ homePageId: string | null }>(
    `${BASE}/pages/${encodeURIComponent(pageId)}/home`,
  );
  return data;
}

/** Retire la page d'accueil du tenant (`homePageId` → null). */
export async function unsetHomePage(): Promise<{
  homePageId: string | null;
}> {
  const { data } = await api.delete<{ homePageId: string | null }>(
    `${BASE}/home`,
  );
  return data;
}
